import CreateCampaignPreview from "../../CreateCampaign/CampaignPreview";
import {
  Button,
  HStack,
  Spinner,
  Stack,
  Text,
  useMediaQuery,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import actions from "src/actions";
import { VersionedTemplates } from "common/email-templates";
import { KYDOrganizationFull } from "@common/interfaces/KYDOrganization";
import {
  KYDBlockData,
  KYDInternalCampaign,
} from "@common/interfaces/KYDCampaign";
import Metrics from "./Metrics";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowBackIcon, ChevronDownIcon } from "@chakra-ui/icons";
import KYDMenuButton from "src/ui/KYDMenuButton";
import { formatAddress } from "common/services/util/address";
import { KYDScope } from "@common/interfaces/ICognito";
import { render } from "src/Manager/Campaigns/utils/emailRenderer";

export interface FormValues {
  title: string;
  additional_audiences?: string[];
  subject: string;
  subject_preview: string;
  preheader?: string;
  send_at?: string;
  blocks: KYDBlockData[];
}

const CampaignMetrics = ({
  permissions = [],
}: {
  permissions?: KYDScope[];
}) => {
  const [view, setView] = useState<"desktop" | "mobile">("desktop");
  const [organization, setOrganization] = useState<KYDOrganizationFull>();
  const [emailHtml, setEmailHtml] = useState<string>("");
  const [campaign, setCampaign] = useState<KYDInternalCampaign>();
  const isMobile = useMediaQuery("(max-width: 768px)")[0];
  const toast = useToast();
  const { id } = useParams();
  const [loading, setLoading] = useState({
    organizationUsers: true,
    sendingTestEmail: false,
    organization: true,
    campaign: true,
  });
  const [isDuplicating, setIsDuplicating] = useState(false);
  const navigate = useNavigate();

  const duplicateCampaign = useCallback(async () => {
    try {
      setIsDuplicating(true);
      const { id: newCampaignId } = await actions.ADMIN.duplicateCampaign(id);
      toast({
        title: "Campaign duplicated",
        description:
          "Campaign duplicated successfully. You're now editing the new campaign.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      console.log("[duplicateCampaign] newCampaignId", newCampaignId);
      navigate(`/campaigns/create?id=${newCampaignId}`);
    } catch (err) {
      console.error(err);
    } finally {
      setIsDuplicating(false);
    }
  }, []);

  const handleDelete = useCallback(async () => {
    try {
      setIsDuplicating(true);
      await actions.ADMIN.deleteCampaign(campaign.id);
      toast({
        title: "Campaign deleted",
        description: "Campaign deleted successfully",
        status: "success",
      });
      navigate(`/campaigns`);
    } catch (error) {
      console.error("Error deleting campaign:", error);
      toast({
        title: "Error deleting campaign",
        description: error.message,
        status: "error",
      });
    }
    setIsDuplicating(false);
  }, [campaign]);

  const fetchOrganization = useCallback(async () => {
    setLoading((prev) => ({ ...prev, organization: true }));
    try {
      const organization = await actions.ADMIN.fetchOrganization();
      setOrganization(organization);
    } catch (err) {
      console.error(err);
    }
    setLoading((prev) => ({ ...prev, organization: false }));
  }, []);

  const fetchCampaign = useCallback(async () => {
    setLoading((prev) => ({ ...prev, campaign: true }));
    try {
      const campaign = await actions.ADMIN.fetchMarketingCampaign(id);
      setCampaign(campaign);
    } catch (err) {
      console.error(err);
    }
    setLoading((prev) => ({ ...prev, campaign: false }));
  }, [id]);

  useEffect(() => {
    const renderEmail = async () => {
      if (!campaign) return;

      const TemplateComponent =
        VersionedTemplates.NewCampaign[campaign.template_version || 1];

      if (!TemplateComponent) {
        console.error(
          `Template version ${campaign.template_version} not found`
        );
        return;
      }

      const { response: emailHtml } = await render({
        template: TemplateComponent,
        version: campaign.template_version || 1,
        props: {
          address: formatAddress(organization?.address),
          subject: campaign?.subject,
          preheader: campaign?.preheader,
          blocks: campaign.blocks,
          force_desktop: view === "desktop",
          related_shows: [],
          organization_logo_url: organization?.logo_url,
          organization_website: "",
          socials: organization?.socials,
        },
      });

      setEmailHtml(emailHtml);
    };
    renderEmail();
  }, [campaign]);

  const onCancelCampaign = useCallback(async () => {
    try {
      await actions.ADMIN.cancelCampaign(campaign?.id);
      if (campaign?.status === "ACTIVE") {
        toast({
          title: "Campaign cancelled",
          description: "Campaign cancelled successfully",
          status: "success",
        });
        return;
      }

      if (campaign?.status === "OPEN") {
        navigate(`/campaigns/create?id=${campaign?.id}`);
        return;
      }
    } catch (err) {
      console.error(err);
      toast({
        title: "Error cancelling campaign",
        description: err.message,
        status: "error",
      });
    }
  }, [campaign]);

  useEffect(() => {
    fetchOrganization();
    fetchCampaign();
  }, [fetchOrganization, fetchCampaign]);

  if (loading.organization || loading.campaign) {
    return <Spinner />;
  }
  return (
    <VStack>
      <HStack w="100%" h="100%" justifyContent="space-between">
        <VStack alignItems="flex-start">
          <Link to="/campaigns">
            <Button
              variant="link"
              color="#888888"
              fontWeight="500"
              fontSize="14px"
              leftIcon={<ArrowBackIcon />}
            >
              All Campaigns
            </Button>
          </Link>
          <Text
            fontSize="24px"
            color="#1A202C"
            overflow="hidden"
            textOverflow="ellipsis"
            lineHeight="30px"
            letterSpacing="-0.72px"
          >
            {campaign.name}
          </Text>
        </VStack>
        <KYDMenuButton
          isLoading={isDuplicating}
          title="Actions"
          options={
            campaign.status === "DRAFT"
              ? [
                  {
                    label: "Duplicate Email Campaign",
                    onClick: duplicateCampaign,
                  },
                  { label: "Delete Draft", onClick: handleDelete },
                ]
              : [
                  {
                    label: "Duplicate Email Campaign",
                    onClick: duplicateCampaign,
                  },
                ]
          }
        />
      </HStack>
      <Stack
        w="100%"
        h="100%"
        direction={{ base: "column", md: "row" }}
        gap={12}
        justifyContent="flex-start"
        alignItems="flex-start"
      >
        <Metrics
          permissions={permissions}
          isMobile={isMobile}
          campaign={campaign}
          onCancelCampaign={onCancelCampaign}
        />
        <CreateCampaignPreview
          showPreview={true}
          emailHtml={emailHtml || ""}
          view={view}
          setView={setView}
          isMobile={false}
        />
      </Stack>
    </VStack>
  );
};

export default CampaignMetrics;
